import * as R from "ramda";
import SongService from "@/services/Song";
import { DRUMS, INSTRUMENTS, MIXES, SOUND_EFFECTS } from "@/constants";

const API_VERSION = 3;

const CREATION_STATUSES = {
  queue: "queue",
  compose: "composing_instrumental",
  produce: "recording_instrumental",
  complete: "complete"
};

const REUSE_DEFAULT_VALUE = "none";
const DEFAULT_TEMPO = 90;
const VOCAL_OPTIONS_COMPOSITION_EFFECT_DEFAULT = "v2_autovocal";
const VOCAL_OPTIONS_PITCH_EFFECT_DEFAULT = "avpr_mild";
const VOCAL_OPTIONS_PRODUCTION_EFFECT_DEFAULT = "fxch_av_autotune_w_reverb";

const getDefaultVocalOptions = (autoVocal = true) => {
  if (autoVocal) {
    return {
      compositionEffect: VOCAL_OPTIONS_COMPOSITION_EFFECT_DEFAULT,
      pitchEffect: VOCAL_OPTIONS_PITCH_EFFECT_DEFAULT,
      productionEffect: VOCAL_OPTIONS_PRODUCTION_EFFECT_DEFAULT
    };
  } else {
    return {
      productionEffect: VOCAL_OPTIONS_PRODUCTION_EFFECT_DEFAULT
    };
  }
};

const initialState = {
  style: null,
  filter: null,
  advanced: false,
  intensity: 5,
  drums: 1,
  bass: 1,
  lead: 1,
  chord: 1,
  influence: "hip hop",
  tempo: DEFAULT_TEMPO,
  drum_density: 0.5,
  lead_density: 0.5,
  bass_density: 0.5,
  chord_density: 0.5,
  humanness: 0.1,
  song_name: "",
  artist_name: "",
  creativity: 5,
  instrument_ensemble: INSTRUMENTS.whole_db_harmonic.value,
  drum_ensemble: DRUMS.whole_db_drums.value,
  mix: MIXES.clean.value,
  sfx: SOUND_EFFECTS.none.value,
  private: false,
  tags: [],
  notes: null,
  reuseProductionId: REUSE_DEFAULT_VALUE,
  reuseCompositionId: REUSE_DEFAULT_VALUE,
  pendingSongIds: [],
  unplayedSongIds: [],
  currentSongId: null,
  fetchingSongs: false,
  fetchingSongIds: false,
  enabled: true,
  trackingSongs: false,
  vocalFileId: null,
  pendingVocalId: null,
  fetchingVocal: false,
  autoVocal: true,
  trackingVocal: false,
  vocalError: null,
  tagSearchResults: [],
  creationStatus: CREATION_STATUSES.queue,
  createError: false,
  vocalEmphasis: {},
  vocalOptions: getDefaultVocalOptions(true)
};
const state = R.clone(initialState);
const mutations = {
  SET_CREATE_ERROR(state, hasError) {
    state.createError = hasError;
  },
  SET_SONG_STYLE(state, style) {
    state.style = style;
  },
  SET_STYLE(state, { style, filter, advanced }) {
    state.style = style;
    state.filter = filter;
    state.advanced = advanced;
  },
  SET_SONG_NAME(state, name) {
    state.song_name = name;
  },
  SET_FILTER(state, filter) {
    state.filter = filter;
  },
  SET_ARTIST_NAME(state, artist) {
    state.artist_name = artist;
  },
  SET_PRIVATE(state, isPrivate) {
    state.private = isPrivate;
  },
  SET_TAGS(state, tags) {
    state.tags = tags;
  },
  SET_ADVANCED(state) {
    state.advanced = true;
  },
  SET_INTENSITY(state, value) {
    state.intensity = value;
  },
  SET_DRUMS(state, value) {
    state.drums = value;
  },
  SET_BASS(state, value) {
    state.bass = value;
  },
  SET_LEAD(state, value) {
    state.lead = value;
  },
  SET_INFLUENCE(state, value) {
    state.influence = value;
  },
  SET_TEMPO(state, value) {
    state.tempo = value;
  },
  SET_DRUM_DENSITY(state, value) {
    state.drum_density = value;
  },
  SET_LEAD_DENSITY(state, value) {
    state.lead_density = value;
  },
  SET_BASS_DENSITY(state, value) {
    state.bass_density = value;
  },
  SET_CHORD_DENSITY(state, value) {
    state.chord_density = value;
  },
  SET_HUMANNESS(state, value) {
    state.humanness = value;
  },
  SET_SPECIFIC_BASS(state, value) {
    state.specific_bass = value;
  },
  SET_SPECIFIC_LEAD(state, value) {
    state.specific_lead = value;
  },
  SET_NOTES(state, value) {
    state.notes = value;
  },
  SET_CURRENT_SONG_ID(state, value) {
    state.currentSongId = value;
  },
  SET_FETCHING_SONGS(state, value) {
    state.fetchingSongs = value;
  },
  SET_FETCHING_SONG_IDS(state, value) {
    state.fetchingSongIds = value;
  },
  SET_TRACKING_SONGS(state, value) {
    state.trackingSongs = value;
  },
  ADD_PENDING_SONG_ID(state, id) {
    state.pendingSongIds.push(id);
  },
  REMOVE_PENDING_SONG_ID(state, id) {
    const index = state.pendingSongIds.indexOf(parseInt(id));
    if (index !== -1) {
      state.pendingSongIds.splice(index, 1);
    }
  },
  CLEAR_PENDING_SONGS(state) {
    state.pendingSongIds = [];
  },
  ADD_UNPLAYED_SONG_ID(state, id) {
    state.unplayedSongIds.push(parseInt(id));
    state.creationStatus = CREATION_STATUSES.complete;
  },
  MARK_SONG_PLAYED(state, id) {
    const index = state.unplayedSongIds.indexOf(parseInt(id));
    if (index !== -1) {
      state.unplayedSongIds.splice(index, 1);
    }
  },
  MARK_ALL_PLAYED_RESET(state) {
    state.unplayedSongIds = [];
    state.creationStatus = CREATION_STATUSES.queue;
    state.createError = false;
  },
  MARK_ALL_PLAYED(state) {
    state.unplayedSongIds = [];
  },
  SET_VOCAL_FILE_ID(state, id) {
    state.vocalFileId = id;
  },
  SET_FETCHING_VOCAL(state, fetching) {
    state.fetchingVocal = fetching;
  },
  SET_PENDING_VOCAL_ID(state, id) {
    state.pendingVocalId = parseInt(id);
  },
  CLEAR_PENDING_VOCAL_ID(state) {
    state.pendingVocalId = null;
  },
  SET_AUTO_VOCAL(state, enabled) {
    state.autoVocal = enabled;
  },
  SET_TRACKING_VOCAL(state, isTracking) {
    state.trackingVocal = isTracking;
  },
  RESET_VOCAL_STATE(state) {
    state.vocalFileId = null;
    state.pendingVocalId = null;
    state.fetchingVocal = false;
    state.autoVocal = true;
    state.trackingVocal = false;
    state.vocalError = null;
    state.vocalOptions = getDefaultVocalOptions(true);
    state.vocalEmphasis = {};
  },
  SET_TAG_SEARCH_RESULTS(state, results) {
    let resultList = [];
    results.map(e => resultList.push(e.replace("#", "")));
    state.tagSearchResults = results;
  },
  SET_CREATION_STATUS(state, status) {
    if (Object.values(CREATION_STATUSES).indexOf(status) !== -1) {
      state.creationStatus = status;
    }
  },
  SET_INSTRUMENT_ENSEMBLE(state, ensemble) {
    state.instrument_ensemble = ensemble;
  },
  SET_DRUM_ENSEMBLE(state, ensemble) {
    state.drum_ensemble = ensemble;
  },
  SET_MIX(state, mix) {
    state.mix = mix;
  },
  SET_SFX(state, sfx) {
    state.sfx = sfx;
  },
  SET_REUSE_COMP_ID(state, id) {
    state.reuseCompositionId = id;
  },
  SET_REUSE_PROD_ID(state, id) {
    state.reuseProductionId = id;
  },
  RESET_STATE(state) {
    Object.assign(state, initialState);
  },
  SET_VOCAL_ERROR(state, error) {
    state.vocalError = error;
  },
  SET_VOCAL_EMPHASIS(state, vocalEmphasis) {
    state.vocalEmphasis = vocalEmphasis;
  },
  SET_VOCAL_OPTIONS(state, vocalOptions) {
    state.vocalOptions = vocalOptions;
  }
};
const actions = {
  setCurrentSongId({ commit }, id) {
    commit("SET_CURRENT_SONG_ID", id);
  },
  setVocalFileId({ commit, dispatch, state }, id) {
    commit("SET_VOCAL_FILE_ID", id);
  },
  async setVocalParametersAndCreate(
    { commit, dispatch, state },
    { autoVocal, emphasis, options }
  ) {
    commit("SET_AUTO_VOCAL", autoVocal);
    commit("SET_VOCAL_EMPHASIS", emphasis);
    commit("SET_VOCAL_OPTIONS", options);
    if (this.vocalFileId !== null) {
      await dispatch("createVocal");
    }
    return;
  },
  resetState({ commit }) {
    commit("RESET_STATE");
  },
  resetVocalState({ commit }) {
    commit("RESET_VOCAL_STATE");
  },
  setAutoVocal({ commit }, enabled) {
    commit("SET_AUTO_VOCAL", enabled);
  },
  setCreationStyle(context, style) {
    context.commit("SET_STYLE", style);
  },
  setTempo({ commit }, tempo) {
    commit("SET_TEMPO", tempo);
  },
  async renderSong({ commit, state, dispatch }, notes) {
    const currentSongId = state.currentSongId;
    commit("MARK_ALL_PLAYED");
    commit("CLEAR_PENDING_SONGS");
    commit("SET_ADVANCED");
    commit("SET_NOTES", notes);
    commit("SET_REUSE_PROD_ID", currentSongId);
    commit("SET_REUSE_COMP_ID", currentSongId);
    await dispatch("createMoreSongs", 1);
    commit("SET_CURRENT_SONG_ID", null);
  },
  async rewriteSong({ commit, state, dispatch }, { style, filter }) {
    const currentSongId = state.currentSongId;
    commit("MARK_ALL_PLAYED");
    commit("CLEAR_PENDING_SONGS");
    commit("SET_REUSE_PROD_ID", currentSongId); //get new comp, reuse production
    commit("SET_STYLE", {
      style,
      filter,
      advanced: state.advanced
    });
    await dispatch("createMoreSongs", 1);
    commit("SET_CURRENT_SONG_ID", null);
  },
  async reproduceSong({ commit, state, dispatch }) {
    const currentSongId = state.currentSongId;
    commit("MARK_ALL_PLAYED");
    commit("CLEAR_PENDING_SONGS");
    await commit("SET_REUSE_COMP_ID", currentSongId); //get new production, reuse comp
    await dispatch("createMoreSongs", 1);
    commit("SET_CURRENT_SONG_ID", null);
  },
  setAdvancedOptions(context) {
    context.commit("SET_ADVANCED");
  },
  addPendingSongId({ dispatch, commit }, id) {
    commit("ADD_PENDING_SONG_ID", id);
  },
  clearPendingSongs(context) {
    context.commit("CLEAR_PENDING_SONGS");
  },
  removePendingSongId(context, id) {
    context.commit("REMOVE_PENDING_SONG_ID", id);
  },
  addUnplayedSongId(context, id) {
    context.commit("ADD_UNPLAYED_SONG_ID", id);
  },
  setTracking({ commit }, value) {
    commit("SET_TRACKING_SONGS", value);
  },
  markSongPlayed(context, id) {
    context.commit("MARK_SONG_PLAYED", id);
  },
  markAllPlayed(context) {
    context.commit("MARK_ALL_PLAYED_RESET");
  },
  setSongInfo({ state, commit }, song) {
    commit("SET_SONG_NAME", song.songName || "");
    commit("SET_ARTIST_NAME", song.artistName || "");
    commit("SET_PRIVATE", song.private);
    commit("SET_TAGS", song.tags);
    commit("SET_AUTO_VOCAL", song.autovocal || false);
    commit("SET_VOCAL_EMPHASIS", song.avEmphasis || {});
    commit(
      "SET_VOCAL_OPTIONS",
      song.vocalOptions || getDefaultVocalOptions(state.autoVocal)
    );
    commit("SET_VOCAL_FILE_ID", song.vocalFileId || null);
    commit("SET_TEMPO", song.tempo);
  },
  async setCreatorSongInfo({ commit, state, dispatch }, song) {
    return new Promise((resolve, reject) => {
      dispatch("setSongInfo", song);
      if (song.style && song.style.model && song.style.songStyle) {
        dispatch("setCreationStyle", {
          style: song.style.model,
          filter: song.style.songStyle,
          advanced: song.style.advanced
        });
      }
      resolve();
    });
  },
  maybeUpdateStatus({ commit, state }, songStatus) {
    switch (state.creationStatus) {
      case CREATION_STATUSES.complete:
        return;
      case CREATION_STATUSES.produce:
      case CREATION_STATUSES.compose:
      case CREATION_STATUSES.queue:
        const currentStepIndex = Object.values(CREATION_STATUSES).indexOf(
          state.creationStatus
        );
        const nextStepIndex = Object.values(CREATION_STATUSES).indexOf(
          songStatus
        );
        if (nextStepIndex > currentStepIndex) {
          commit("SET_CREATION_STATUS", songStatus);
        }
        break;
    }
  },
  playNextOrCreate({ dispatch, commit, state }, amplitudeInstance) {
    if (state.currentSongId && amplitudeInstance) {
      amplitudeInstance.getInstance().logEvent("create_reject", {
        song_id: state.currentSongId,
        style: state.style,
        filter: state.filter,
        advanced: state.advanced
      });
    }
    if (state.notes) {
      state.notes = null;
      state.reuseProductionId = REUSE_DEFAULT_VALUE;
      state.reuseCompositionId = REUSE_DEFAULT_VALUE;
      if (!state.advanced) {
        state.tempo = 120;
      }
    }
    const unplayedSongIds = R.clone(state.unplayedSongIds);
    if (unplayedSongIds.length > 0) {
      const newSongId = R.clone(unplayedSongIds[0]);
      commit("SET_CURRENT_SONG_ID", newSongId);
      commit("MARK_SONG_PLAYED", newSongId);
      if (
        unplayedSongIds.length <= 2 &&
        state.pendingSongIds.length <= 2 &&
        !state.fetchingSongIds
      ) {
        dispatch("createMoreSongs", 3);
      }
    } else {
      if (state.pendingSongIds.length <= 2 && !state.fetchingSongIds) {
        dispatch("createMoreSongs", 3);
      }
      commit("SET_CURRENT_SONG_ID", null);
    }
  },
  async cancelVocal({ commit }) {
    commit("CLEAR_PENDING_VOCAL_ID");
  },
  async createVocal({ dispatch, commit, state }) {
    commit("SET_FETCHING_VOCAL", true);
    commit("SET_VOCAL_ERROR", null);

    let result = await SongService.createVocal(
      state.currentSongId,
      state.vocalFileId,
      state.autoVocal,
      state.vocalEmphasis,
      state.vocalOptions
    );

    if (result.status === 200) {
      commit("SET_PENDING_VOCAL_ID", state.currentSongId);
      dispatch("trackVocal");
    } else {
      commit("SET_FETCHING_VOCAL", false);
      commit("SET_PENDING_VOCAL_ID", null);
      commit("SET_VOCAL_ERROR", "Unable to create vocal track");
    }

    return;
  },
  async fetchSong({ dispatch, commit, state }, songId) {
    if (state.enabled) {
      commit("SET_FETCHING_SONGS", true); //TODO: add fetching state and getters/setters
      await SongService.getSongForEdit({ songId, removeNotes: true }).then(
        result => {
          if (result.status === 200 && result.data) {
            dispatch("setCreatorSongInfo", result.data);
            dispatch("song/addSong", result.data, { root: true });
            commit("REMOVE_PENDING_SONG_ID", result.data.id);
            commit("ADD_UNPLAYED_SONG_ID", result.data.id);
            commit("SET_FETCHING_SONGS", false);
            if (state.pendingVocalId === songId) {
              commit("SET_PENDING_VOCAL_ID", null);
              commit("SET_FETCHING_VOCAL", false);
              commit("SET_VOCAL_ERROR", null);
            }
          }
        },
        error => {
          commit("SET_FETCHING_SONGS", false);
          console.log(error);
        }
      );
    }
  },
  createMoreSongs({ dispatch, commit }, num) {
    commit("SET_FETCHING_SONG_IDS", true);
    let createdSong = false;
    let pendingSongs = num;
    for (let i = 0; i < num; i++) {
      dispatch("createSong").then(success => {
        if (success && !createdSong) {
          createdSong = true;
        }
        pendingSongs -= 1;
        if (pendingSongs === 0) {
          commit("SET_CREATE_ERROR", !createdSong);
          commit("SET_FETCHING_SONG_IDS", false);
        }
      });
    }
  },
  createSong({ commit, state }) {
    return new Promise(resolve => {
      const createParams = {
        tempo:
          state.advanced || state.reuseProductionId
            ? Math.round(state.tempo)
            : 120,
        drums: state.advanced ? state.drums : 1,
        bass: state.advanced ? state.bass : 1,
        lead: state.advanced ? state.lead : 1,
        chord: state.advanced ? state.chord : 1,
        style: state.style,
        filter: state.filter,
        api_version: API_VERSION,
        songs: 1,
        re_p: state.reuseProductionId,
        re_c: state.reuseCompositionId,
        intensity: state.advanced ? Math.round(state.intensity) : 5,
        advanced: state.advanced ? 1 : 0,
        drum_density: state.drum_density,
        lead_density: state.lead_density,
        bass_density: state.bass_density,
        chord_density: state.chord_density,
        humanness: state.humanness
      };

      if (state.notes) {
        createParams.notes = state.notes;
      }

      if (state.instrument_ensemble) {
        createParams.instrument_ensemble = state.instrument_ensemble;
      }
      if (state.drum_ensemble) {
        createParams.drum_ensemble = state.drum_ensemble;
      }
      if (state.mix) {
        createParams.mixing_style = state.mix;
      }
      if (state.sfx) {
        createParams.sfx = state.sfx;
      }

      SongService.createSong(createParams).then(
        result => {
          if (state.enabled && result.status === 200 && result.data) {
            commit("ADD_PENDING_SONG_ID", result.data);
            commit("SET_REUSE_COMP_ID", REUSE_DEFAULT_VALUE);
            commit("SET_REUSE_PROD_ID", REUSE_DEFAULT_VALUE);
          }
          resolve(result.status === 200);
        },
        () => {
          resolve(false);
        }
      );
    });
  },
  async trackSongs({ dispatch, commit, state }) {
    commit("SET_TRACKING_SONGS", true);
    const pendingIds = state.pendingSongIds;
    if (state.enabled && pendingIds.length > 0) {
      await SongService.postTrackedSongs({ songIds: pendingIds }).then(
        result => {
          if (result.status === 200 && result.data) {
            result.data.forEach(song => {
              if (
                song.status === "complete" &&
                pendingIds.indexOf(parseInt(song.id)) !== -1
              ) {
                dispatch("maybeUpdateStatus", song.status);
                dispatch("fetchSong", song.id);
              } else if (song.status === "timeout") {
                commit("REMOVE_PENDING_SONG_ID", song.id); // timed out
                if (
                  state.pendingSongIds.length === 0 &&
                  state.unplayedSongIds.length === 0 &&
                  !state.currentSongId
                ) {
                  commit("SET_CREATE_ERROR", true);
                }
              } else {
                dispatch("maybeUpdateStatus", song.status);
              }
            });
            if (pendingIds.length > 0) {
              setTimeout(() => {
                dispatch("trackSongs");
              }, 2500);
            } else {
              commit("SET_TRACKING_SONGS", false);
            }
          }
        },
        error => {
          if (
            error.response &&
            error.response.status !== 400 &&
            pendingIds.length > 0
          ) {
            setTimeout(() => {
              dispatch("trackSongs");
            }, 2500);
          } else {
            commit("SET_TRACKING_SONGS", false);
          }
        }
      );
    } else {
      commit("SET_TRACKING_SONGS", false);
    }
  },
  async trackVocal({ dispatch, commit, state }) {
    commit("SET_TRACKING_VOCAL", true);
    const id = state.pendingVocalId;
    if (id && state.enabled) {
      await SongService.postTrackedSongs({ songIds: [id], vocals: true }).then(
        result => {
          if (result.status === 200 && result.data) {
            result.data.forEach(song => {
              if (song.status === "complete") {
                dispatch("fetchSong", parseInt(song.id));
              } else if (song.status === "timeout") {
                commit("SET_PENDING_VOCAL_ID", null); // timed out
                commit("SET_FETCHING_VOCAL", false);
                commit("SET_VOCAL_ERROR", "Timed out waiting for vocal");
              }
            });
            if (state.pendingVocalId) {
              setTimeout(() => {
                dispatch("trackVocal");
              }, 2500);
            } else {
              commit("SET_TRACKING_VOCAL", false);
              commit("SET_FETCHING_VOCAL", false);
            }
          }
        },
        error => {
          if (
            error.response &&
            error.response.status !== 400 &&
            state.pendingVocalId
          ) {
            setTimeout(() => {
              dispatch("trackVocal");
            }, 2500);
          } else {
            commit("SET_TRACKING_VOCAL", false);
            commit("SET_FETCHING_VOCAL", false);
          }
        }
      );
    } else {
      commit("SET_TRACKING_VOCAL", false);
      commit("SET_FETCHING_VOCAL", false);
    }
  },
  checkUnplayedSongs({ commit, state }) {
    if (state.unplayedSongIds.length > 0 && state.currentSongId === null) {
      commit("SET_CURRENT_SONG_ID", state.unplayedSongIds[0]);
      commit("MARK_SONG_PLAYED", state.unplayedSongIds[0]);
    }
  },
  async fetchAutoVocal({ dispatch, commit, state }) {
    commit("SET_FETCHING_AUTO_VOCAL", true); //TODO: add fetching state and getters/setters
    await SongService.postAutoVocal({
      songId: state.currentSongId,
      vocalFileId: state.vocalFileId
    }).then(
      result => {
        if (result.status === 200 && result.data) {
          dispatch("song/addSong", result.data, { root: true });
          commit("REMOVE_PENDING_SONG_ID", result.data.id);
          commit("ADD_UNPLAYED_SONG_ID", result.data.id);
          commit("SET_FETCHING_SONGS", false);
        }
      },
      error => {
        commit("SET_FETCHING_SONGS", false);
        console.log(error);
      }
    );
  },
  async fetchTagSearchResults({ commit }, query) {
    const res = await SongService.getTags(query.replace("#", ""));
    if (res.status === 200 && res.data) {
      commit("SET_TAG_SEARCH_RESULTS", res.data);
    }
  },
  createError({ commit }, value) {
    commit("SET_CREATE_ERROR", value);
  }
};
const getSongCreated = state =>
  state.creationStatus === CREATION_STATUSES.complete;
const getters = {
  getDefaultVocalOptions() {
    return getDefaultVocalOptions(true);
  },
  getSongStyle(state) {
    return state.style;
  },
  getAdvStyle(state) {
    return state.adv_style;
  },
  getProductionFilter(state) {
    return state.filter;
  },
  getAdvanced(state) {
    return state.advanced;
  },
  getIntensity(state) {
    return state.intensity;
  },
  getDrums(state) {
    return state.drums;
  },
  getBass(state) {
    return state.bass;
  },
  getLead(state) {
    return state.lead;
  },
  getInfluence(state) {
    return state.influence;
  },
  getTempo(state) {
    return state.tempo;
  },
  getDrumDensity(state) {
    return state.drum_density;
  },
  getLeadDensity(state) {
    return state.lead_density;
  },
  getBassDensity(state) {
    return state.bass_density;
  },
  getChordDensity: state => state.chord_density,
  getHumanness(state) {
    return state.humanness;
  },
  getSpecificBass(state) {
    return state.specific_bass;
  },
  getSpecificLead(state) {
    return state.specific_lead;
  },
  getSongName(state) {
    return state.song_name;
  },
  getArtistName(state) {
    return state.artist_name;
  },
  getPrivate(state) {
    return state.private;
  },
  getTags(state) {
    return state.tags;
  },
  getSongInfo: state => ({
    title: state.song_name,
    artistName: state.artist_name,
    private: state.private,
    tags: state.tags
  }),
  getPendingSongIds: state => state.pendingSongIds,
  getUnplayedSongIds: state => state.unplayedSongIds,
  getCurrentSongId: state => state.currentSongId,
  getTrackingSongs: state => state.trackingSongs,
  getFetchingSongs: state => state.fetchingSongs,
  getFetchingSongIds: state => state.fetchingSongIds,
  getSongCreated,
  getSongCreationStep: state =>
    Object.values(CREATION_STATUSES).indexOf(state.creationStatus),
  getCreatingMore: state =>
    getSongCreated(state) && state.currentSongId === null,
  getVocalFileId: state => state.vocalFileId,
  getFetchingVocal: state => state.fetchingVocal,
  getAutoVocal: state => state.autoVocal,
  getTagSearchResults: state =>
    state.tagSearchResults.map(tag => {
      return {
        title: "#" + tag,
        value: tag
      };
    }),
  getInstrumentEnsemble: state => state.instrument_ensemble,
  getDrumEnsemble: state => state.drum_ensemble,
  getMix: state => state.mix,
  getSfx: state => state.sfx,
  getCreatingDerivativeSong: state =>
    state.reuseProductionId !== REUSE_DEFAULT_VALUE ||
    state.reuseCompositionId !== REUSE_DEFAULT_VALUE,
  getCreateError: state => state.createError,
  getVocalError: state => state.vocalError
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
